(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function splitWords(el) {
    if (!el || el.dataset.wordsReady) return;
    const text = el.textContent.trim();
    el.setAttribute('aria-label', text);
    el.innerHTML = text
      .split(/(\s+)/)
      .map((part, i) => {
        if (/^\s+$/.test(part)) return part;
        return `<span class="why-word" style="--i:${i}">${part}</span>`;
      })
      .join('');
    el.dataset.wordsReady = '1';
  }

  function animateCounter(root) {
    const valueEl = root.querySelector('[data-counter-value]');
    if (!valueEl || root.dataset.counted === '1') return;
    const to = Number(root.dataset.countTo || 0);
    const suffix = root.dataset.suffix || '';
    if (reduce) {
      valueEl.textContent = `${to.toLocaleString()}${suffix}`;
      root.dataset.counted = '1';
      return;
    }
    const duration = 1600;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = Math.round(to * eased);
      valueEl.textContent = `${current.toLocaleString()}${suffix}`;
      if (t < 1) requestAnimationFrame(tick);
      else root.dataset.counted = '1';
    };
    requestAnimationFrame(tick);
  }

  function initSpotlight(section) {
    const root = section.querySelector('[data-why-spot]');
    if (!root) return;

    const items = [...root.querySelectorAll('[data-spot-item]')];
    const tabs = [...root.querySelectorAll('[data-spot-tab]')];
    const panels = [...root.querySelectorAll('.why-spot__stage [data-spot-panel]')];
    const progress = root.querySelector('[data-spot-progress]');
    const hint = root.querySelector('[data-spot-hint]');
    const nav = root.querySelector('.why-spot__nav');
    let index = 0;

    const scrollTabIntoView = (i) => {
      const item = items[i];
      if (!item || !nav) return;
      const navRect = nav.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      if (itemRect.left < navRect.left || itemRect.right > navRect.right) {
        item.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
      }
    };

    const activate = (next) => {
      index = Math.max(0, Math.min(items.length - 1, next));

      items.forEach((item, n) => item.classList.toggle('is-active', n === index));
      tabs.forEach((tab, n) => {
        tab.setAttribute('aria-selected', n === index ? 'true' : 'false');
        tab.setAttribute('tabindex', n === index ? '0' : '-1');
      });

      panels.forEach((panel, n) => {
        const on = n === index;
        panel.classList.toggle('is-active', on);
        panel.hidden = !on;
      });

      if (progress) {
        progress.style.width = `${((index + 1) / items.length) * 100}%`;
      }

      if (hint) {
        hint.textContent = `Pillar ${index + 1} of ${items.length} — swipe for more`;
      }

      scrollTabIntoView(index);
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activate(i));
      tab.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          activate(index + 1);
          tabs[Math.min(index, tabs.length - 1)]?.focus();
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          activate(index - 1);
          tabs[Math.max(index, 0)]?.focus();
        }
      });
    });

    activate(0);
  }

  function initMagnetic(btn) {
    if (reduce || !btn) return;
    btn.addEventListener('pointermove', (e) => {
      const rect = btn.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      btn.style.setProperty('--rx', `${x}%`);
      btn.style.setProperty('--ry', `${y}%`);
      const dx = (e.clientX - (rect.left + rect.width / 2)) * 0.12;
      const dy = (e.clientY - (rect.top + rect.height / 2)) * 0.12;
      btn.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
    });
    btn.addEventListener('pointerleave', () => {
      btn.style.transform = '';
    });
  }

  function initParallax(layer) {
    if (reduce || !layer) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const rect = layer.parentElement.getBoundingClientRect();
        const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        const y = (progress - 0.5) * 36;
        layer.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0)`;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initSection(section) {
    splitWords(section.querySelector('[data-why-words]'));
    initSpotlight(section);
    initParallax(section.querySelector('[data-why-parallax]'));
    section.querySelectorAll('[data-magnetic]').forEach(initMagnetic);

    const revealables = [
      section.querySelector('.why-spot__header'),
      ...section.querySelectorAll('.why-stat'),
      section.querySelector('.why-cta__panel'),
    ].filter(Boolean);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          if (el === section) {
            section.classList.add('is-inview');
            section.querySelectorAll('.why-intro [data-counter]').forEach(animateCounter);
            io.unobserve(section);
            return;
          }
          el.classList.add('is-visible');
          if (el.classList.contains('why-stat')) {
            const counter = el.querySelector('[data-counter]');
            if (counter) animateCounter(counter);
          }
          io.unobserve(el);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -6% 0px' }
    );

    io.observe(section);
    revealables.forEach((el) => io.observe(el));
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-why-mukhlis]').forEach(initSection);
  });
})();
